import { useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { MdAdd, MdOutlineAutorenew, MdOutlineSearch } from "react-icons/md";
import axios from "../config/axiosInstance";
import { SUBSCRIPTIONS } from "../config/apiConfig";
import useDebouncedValue from "../hooks/useDebouncedValue";
import { Badge, Card, EmptyState, ErrorBlock, SkeletonRows } from "../components/crm/Primitives";
import { formatDate, formatMoney, formatNumber } from "../components/crm/crmHelpers";
import ClientLink from "../components/crm/ClientLink";
import Modal from "../components/modals/Modal";
import SubscriptionFormModal from "../components/subscriptions/SubscriptionFormModal";
import SubscriptionPanel from "../components/subscriptions/SubscriptionPanel";
import { STATUS_TABS, STATUS_TONES } from "../components/subscriptions/subscriptionHelpers";

/*
  Assinaturas: todas as recorrências no Asaas, em um lugar só.

  Serve para duas coisas que a ficha do cliente não resolve: criar assinatura sem
  sair procurando a conta (o operador nem abre a ficha) e responder "quem está em
  atraso agora?". As abas mostram quantas há em cada situação, e nunca o total em
  reais: valores somados são só do ADMIN, em Pagamentos.
*/

const ITEMS_PER_PAGE = 20;

const SubscriptionsPage = () => {
  const queryClient = useQueryClient();

  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState(null);

  const debouncedSearch = useDebouncedValue(search, 350);

  const query = useQuery(
    ["subscriptions", "list", status, debouncedSearch, page],
    async () => {
      const params = new URLSearchParams({ page, itemsPerPage: ITEMS_PER_PAGE });
      if (status) params.set("status", status);
      if (debouncedSearch.trim()) params.set("search", debouncedSearch.trim());
      const { data } = await axios.get(`${SUBSCRIPTIONS.LIST}?${params.toString()}`);
      return data.data;
    },
    { keepPreviousData: true }
  );

  const rows = query.data?.subscriptions || [];
  const counts = query.data?.statusCounts || {};
  const total = query.data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / ITEMS_PER_PAGE));
  const countAll = Object.values(counts).reduce((sum, value) => sum + value, 0);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="min-w-0 text-sm text-slate-500">
          Cobrança recorrente dos salões no Asaas. Crie, edite e acompanhe por aqui.
        </p>
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-1.5 rounded-xl bg-pink-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-pink-700"
        >
          <MdAdd size={18} /> Nova assinatura
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {STATUS_TABS.map((tab) => {
          const active = status === tab.value;
          const count = tab.value ? counts[tab.value] || 0 : countAll;

          return (
            <button
              key={tab.value}
              type="button"
              onClick={() => {
                setStatus(tab.value);
                setPage(1);
              }}
              className={`rounded-full px-3 py-1 text-xs font-semibold ring-1 ring-inset transition ${
                active
                  ? "bg-pink-600 text-white ring-pink-600"
                  : "bg-white text-slate-600 ring-slate-200 hover:ring-pink-200"
              }`}
            >
              {tab.label} · {formatNumber(count)}
            </button>
          );
        })}
      </div>

      <Card className="overflow-hidden">
        <div className="border-b border-slate-100 p-3">
          <div className="relative max-w-md">
            <MdOutlineSearch
              size={18}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
              placeholder="Buscar por salão, e-mail ou #id"
              className="w-full rounded-xl border border-slate-200 py-2 pl-9 pr-3 text-sm outline-none focus:border-pink-300 focus:ring-2 focus:ring-pink-100"
            />
          </div>
        </div>

        {query.isLoading ? (
          <div className="p-4">
            <SkeletonRows rows={6} />
          </div>
        ) : query.error ? (
          <div className="p-4">
            <ErrorBlock
              message={
                query.error.response?.data?.message ||
                "Não foi possível listar as assinaturas."
              }
              onRetry={query.refetch}
            />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={MdOutlineAutorenew}
            title="Nenhuma assinatura aqui"
            description="Crie a primeira em Nova assinatura. As feitas pelo app também aparecem nesta lista."
          />
        ) : (
          <ul className="divide-y divide-slate-100">
            {rows.map((row) => (
              <li key={row.id}>
                <button
                  type="button"
                  onClick={() => setSelected(row)}
                  className="flex w-full flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 text-left transition hover:bg-slate-50"
                >
                  <div className="min-w-[12rem] flex-1">
                    <p className="truncate text-sm font-semibold text-slate-900">
                      {row.company?.name || "—"}
                    </p>
                    <p className="truncate text-[11px] text-slate-500">
                      #{row.companyId} · {row.planName}
                      {row.planId === "DIAMOND" ? "" : ` · ${row.numberOfUsers} prof.`} ·{" "}
                      {row.billingLabel}
                    </p>
                  </div>

                  <div className="text-right">
                    <p className="text-sm font-semibold text-slate-900">
                      {formatMoney(row.value)}/mês
                    </p>
                    <p className="text-[11px] text-slate-500">
                      {row.durationMonths ? `${row.durationMonths} meses` : "Sem prazo"}
                      {row.isNegotiatedValue
                        ? row.couponCode
                          ? " · com cupom"
                          : " · negociado"
                        : ""}
                    </p>
                  </div>

                  <div className="w-28 text-right">
                    <p className="text-[11px] text-slate-500">Próxima cobrança</p>
                    <p className="text-xs font-semibold text-slate-800">
                      {row.nextDueDate ? formatDate(row.nextDueDate) : "—"}
                    </p>
                  </div>

                  <div className="w-40 text-right">
                    <Badge tone={STATUS_TONES[row.status] || "neutral"}>{row.statusLabel}</Badge>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}

        {totalPages > 1 ? (
          <div className="flex items-center justify-between border-t border-slate-100 px-4 py-2 text-xs text-slate-500">
            <span>
              Página {page} de {totalPages} · {formatNumber(total)} assinatura(s)
            </span>
            <div className="flex gap-1.5">
              <button
                type="button"
                onClick={() => setPage((value) => Math.max(value - 1, 1))}
                disabled={page <= 1}
                className="rounded-lg border border-slate-200 px-2.5 py-1 font-semibold disabled:opacity-40"
              >
                Anterior
              </button>
              <button
                type="button"
                onClick={() => setPage((value) => Math.min(value + 1, totalPages))}
                disabled={page >= totalPages}
                className="rounded-lg border border-slate-200 px-2.5 py-1 font-semibold disabled:opacity-40"
              >
                Próxima
              </button>
            </div>
          </div>
        ) : null}
      </Card>

      {creating ? (
        <SubscriptionFormModal
          onClose={() => setCreating(false)}
          onSaved={(subscription, company) => {
            setCreating(false);
            queryClient.invalidateQueries(["subscriptions"]);
            setSelected({ ...subscription, company, fresh: true });
          }}
        />
      ) : null}

      {selected ? (
        <SelectedSubscription
          row={selected}
          onClose={() => setSelected(null)}
        />
      ) : null}
    </div>
  );
};

/*
  A assinatura aberta sobre a lista. Quem acabou de criar cai direto aqui, porque o
  passo seguinte é sempre o mesmo: pegar o link e mandar para a cliente.
*/
const SelectedSubscription = ({ row, onClose }) => {
  const company = {
    id: row.companyId,
    name: row.company?.name,
    phoneNumber: row.company?.phoneNumber,
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="md"
      icon={MdOutlineAutorenew}
      title={company.name || `Conta #${company.id}`}
      subtitle={row.fresh ? "Mande o link para a cliente concluir" : `#${company.id}`}
      headerAccessory={
        <ClientLink
          to={`/clientes/${company.id}`}
          hideWhenBlocked
          className="text-xs font-semibold text-pink-700 hover:underline"
        >
          Abrir ficha
        </ClientLink>
      }
    >
      <SubscriptionPanel company={company} embedded />
    </Modal>
  );
};

export default SubscriptionsPage;
